import React, { Component } from 'react';
import { TableContainer, Table, TableBody, TableCell, TableHead, TableRow, Paper, useTheme } from '@mui/material';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { get, take } from 'global/lib/lodash';
import { InView } from 'react-intersection-observer';
import { CenterLoader } from '../../styles';

// Migrated from react-beautiful-dnd to @dnd-kit (Phase 6: PFM-2599).
// Class component preserved so consumers (cloneedition, scorecards,
// manageedition, editions) don't need touching.

const SortableTableRow = ({ id, onClick, children, canDrag }) => {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !canDrag,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? theme.palette.action.hover : undefined,
    // Keep the cursor obvious so users still know rows are draggable.
    cursor: canDrag ? 'grab' : undefined,
  };
  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      {...(canDrag ? attributes : {})}
      {...(canDrag ? listeners : {})}>
      {children}
    </TableRow>
  );
};

// Function component wrapper so we can use hooks (useSensors) inside what is
// otherwise a class component below.
const SortableBody = ({ ids, onDragEnd, children }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <TableBody>{children}</TableBody>
      </SortableContext>
    </DndContext>
  );
};

export default class DndTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: props.items,
      take: props.take || 10,
      takeAmt: props.takeAmt || 10,
    };
    this.onDragEnd = this.onDragEnd.bind(this);
  }

  onDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = this.state.items.findIndex((i) => i.id === active.id);
    const newIndex = this.state.items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const items = arrayMove(this.state.items, oldIndex, newIndex);
    this.setState({ items }, () => {
      this.props.onOrderChange && this.props.onOrderChange(items);
    });
  }

  onSelectRow = (item, e) => {
    this.props.onSelectRow && this.props.onSelectRow(item, e);
  };

  componentDidUpdate(prevProps) {
    if (prevProps.items !== this.props.items) {
      this.setState({ items: this.props.items });
    }
  }

  renderRow = (item) => {
    const { columns } = this.props;
    const canDrag = get(this, 'props.canDrag', true);
    const cells = this.props.renderRow
      ? this.props.renderRow(item)
      : columns.map((col) => {
          const val = col.render ? col.render(item, get(item, col.field)) : get(item, col.field);
          return (
            <TableCell key={col.field} width={col.width}>
              {val}
            </TableCell>
          );
        });

    return (
      <SortableTableRow key={item.id} id={item.id} onClick={(e) => this.onSelectRow(item, e)} canDrag={canDrag}>
        {cells}
      </SortableTableRow>
    );
  };

  render() {
    const { columns } = this.props;
    const showHeader = get(this, 'props.showHeader', true);
    const showBody = get(this, 'props.showBody', true);

    const visible = take(this.state.items, this.state.take);
    const ids = visible.map((i) => i.id);

    return (
      <TableContainer component={Paper}>
        <Table>
          {showHeader && (
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.field} width={col.width}>
                    {col.title}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          {showBody && (
            <SortableBody ids={ids} onDragEnd={this.onDragEnd}>
              {visible.map(this.renderRow)}
            </SortableBody>
          )}
        </Table>
        {this.state.items.length >= this.state.take && (
          <InView onChange={(inView, entry) => this.setState({ take: this.state.take + this.state.takeAmt })}>
            <CenterLoader style={{ overflow: 'hidden' }} />
          </InView>
        )}
      </TableContainer>
    );
  }
}
